import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { handleApiError } from '../utils/errorHandler';
import {
  ActivityType,
  CallResult,
  CreateActivityDto,
  getActivityTypeName
} from '../services/activityService';
import ReminderDateTimePicker from './common/ReminderDateTimePicker';

interface ActivityFormProps {
  entityType: number;
  entityId: number;
  onSubmit: (activity: CreateActivityDto, reminderDateTime: string | null) => Promise<void>;
  onCancel: () => void;
  relatedReminderId?: number;
  initialActivityType?: ActivityType;
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${props => props.fullWidth && 'grid-column: 1 / -1;'}
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
`;

const RequiredLabel = styled(Label)`
  &::after {
    content: ' *';
    color: var(--error-color);
  }
`;

const Select = styled.select<{ hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  background: white;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const Textarea = styled.textarea<{ hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  resize: vertical;
  min-height: 120px;
  font-family: inherit;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: var(--error-color);
  margin-top: 4px;
`;

const ReminderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
`;

const ReminderHint = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    border: 1px solid var(--primary-color);
    &:hover:not(:disabled) { background: transparent; color: var(--primary-color); }
  ` : `
    background: var(--surface-color);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    &:hover:not(:disabled) { background: var(--hover-background); }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActivityForm: React.FC<ActivityFormProps> = ({
  entityType,
  entityId,
  onSubmit,
  onCancel,
  relatedReminderId,
  initialActivityType = ActivityType.PhoneCall
}) => {
  const [formData, setFormData] = useState<CreateActivityDto>({
    entityType,
    entityId,
    activityType: initialActivityType,
    title: '',
    description: '',
    callResult: null,
    durationMinutes: null,
    relatedReminderId
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [reminderDateTime, setReminderDateTime] = useState<string | null>(null);

  // Generate title based on activity type
  useEffect(() => {
    const generateTitle = () => {
      const typeName = getActivityTypeName(formData.activityType);
      const now = new Date().toLocaleString('he-IL', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      return `${typeName} - ${now}`;
    };

    setFormData(prev => ({ ...prev, title: generateTitle() }));
  }, [formData.activityType]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.description.trim()) {
      newErrors.description = 'יש להזין תיאור הפעילות';
    }

    if (formData.activityType === ActivityType.PhoneCall && !formData.callResult) {
      newErrors.callResult = 'יש לבחור תוצאת שיחה';
    }

    if (reminderDateTime && new Date(reminderDateTime).getTime() <= Date.now()) {
      newErrors.reminder = 'תאריך התזכורת חייב להיות בעתיד';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      await onSubmit(formData, reminderDateTime);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בשמירת הפעילות'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFieldChange = (field: keyof CreateActivityDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const showCallResult = formData.activityType === ActivityType.PhoneCall;
  const showDuration = [ActivityType.PhoneCall, ActivityType.Meeting].includes(formData.activityType);

  return (
    <FormContainer>
      <FormGrid>
        <FormField>
          <RequiredLabel>סוג פעילות</RequiredLabel>
          <Select
            value={formData.activityType}
            onChange={(e) => handleFieldChange('activityType', parseInt(e.target.value))}
          >
            <option value={ActivityType.PhoneCall}>שיחת טלפון</option>
            <option value={ActivityType.WhatsApp}>הודעת וואטסאפ</option>
            <option value={ActivityType.Email}>אימייל</option>
            <option value={ActivityType.Meeting}>פגישה</option>
          </Select>
        </FormField>

        {showCallResult && (
          <FormField>
            <RequiredLabel>תוצאת השיחה</RequiredLabel>
            <Select
              value={formData.callResult || ''}
              onChange={(e) => handleFieldChange('callResult', e.target.value ? parseInt(e.target.value) : null)}
              hasError={!!errors.callResult}
            >
              <option value="">בחר תוצאה</option>
              <option value={CallResult.Interested_FollowUp}>מעוניין - צריך מעקב</option>
              <option value={CallResult.Interested_ReadyToSign}>מעוניין - מוכן לחתום</option>
              <option value={CallResult.Undecided_CallBack}>מתלבט</option>
              <option value={CallResult.NotAvailable}>לא זמין</option>
              <option value={CallResult.NotInterested}>לא מעוניין</option>
              <option value={CallResult.NoAnswer}>אין מענה</option>
              <option value={CallResult.WrongNumber}>מספר שגוי</option>
              <option value={CallResult.Other}>אחר</option>
            </Select>
            {errors.callResult && <ErrorText>{errors.callResult}</ErrorText>}
          </FormField>
        )}

        {showDuration && (
          <FormField>
            <Label>משך הפעילות (דקות)</Label>
            <Input
              type="number"
              value={formData.durationMinutes || ''}
              onChange={(e) => handleFieldChange('durationMinutes', e.target.value ? parseInt(e.target.value) : null)}
              placeholder="לדוגמה: 15"
              min="1"
            />
          </FormField>
        )}

        <FormField fullWidth>
          <RequiredLabel>תיאור הפעילות</RequiredLabel>
          <Textarea
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            placeholder="תאר את הפעילות בפירוט - מה נאמר, מה סוכם, הערות חשובות..."
            hasError={!!errors.description}
          />
          {errors.description && <ErrorText>{errors.description}</ErrorText>}
        </FormField>
      </FormGrid>

      <ReminderSection>
        <Label>תזכורת (אופציונלי)</Label>
        <ReminderHint>בחר מועד כדי ליצור תזכורת — או השאר ריק אם אין צורך</ReminderHint>
        <ReminderDateTimePicker
          value={reminderDateTime}
          onChange={setReminderDateTime}
          error={errors.reminder}
        />
      </ReminderSection>

      <ButtonGroup>
        <Button onClick={onCancel} disabled={submitting}>
          ביטול
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'שומר...' : 'שמור פעילות'}
        </Button>
      </ButtonGroup>
    </FormContainer>
  );
};

export default ActivityForm;